// /:partnerId/forgot-password — Auth0 dbconnections/change_password.
//
// Backend responde 200 siempre (anti-enumeracion). La UI nunca confirma si
// el email existe: muestra un mensaje generico "si la cuenta existe,
// recibiras un email".

import { FormEvent, useState } from 'react';
import { normalizeEmail } from '@/lib/email-normalize';
import { Link, useNavigate, useParams } from 'react-router-dom';
import AuthShell from '@/components/AuthShell';
import FormField from '@/components/FormField';
import SubmitButton from '@/components/SubmitButton';
import { useAuthParams } from '@/lib/auth-params';
import { requestPasswordReset, ApiError } from '@/lib/api';
import { webmailLink } from '@/lib/webmail';
import { BackStepLink } from '@/components/FlowNav';

export default function ForgotPassword(): JSX.Element {
  const { partnerId = '' } = useParams<{ partnerId: string }>();
  const navigate = useNavigate();
  const authz = useAuthParams();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function onSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setLoading(true);
    setErr(null);
    try {
      // client_id viene de los authz params (?client_id=...). Si la URL fue
      // accedida directo (sin params), usamos un dummy — el backend acepta
      // igual y silencia (anti-enumeracion).
      const clientId = authz.ok ? authz.params.client_id : 'no-client';
      // Capa 3 — submit: se vuelve a forzar la forma canónica aunque las
      // capas del input ya la hayan aplicado. Es barato e idempotente, y
      // cubre cualquier vía que haya escrito el state sin pasar por el input.
      await requestPasswordReset(partnerId, normalizeEmail(email), clientId);
      setSubmitted(true);
    } catch (e) {
      const code = e instanceof ApiError ? e.code : 'unknown';
      // Solo mostramos error si es un problema 4xx/5xx de shape — no
      // revelamos si el email existe o no.
      setErr(
        code === 'invalid_request_body'
          ? 'El email no es válido.'
          : 'No pudimos procesar el pedido. Probá de nuevo en un momento.',
      );
    } finally {
      setLoading(false);
    }
  }

  if (submitted) {
    const mail = webmailLink(email);
    return (
      <AuthShell
        title="Revisá tu email"
        subtitle="Si existe una cuenta con ese email, te enviamos un link para restablecer la contraseña."
        footerLinks={
          authz.ok ? (
            <Link
              to={`/${partnerId}/login${window.location.search}`}
              className="inline-flex items-center gap-1.5 text-text-muted underline-offset-4 transition hover:text-text hover:underline"
            >
              Volver a ingresar
            </Link>
          ) : null
        }
      >
        <div className="space-y-3">
          <div
            role="status"
            aria-live="polite"
            className="rounded-lg border border-text-muted/20 p-4 text-sm text-text-muted"
          >
            El link vence en 1 hora. Si no lo recibís en unos minutos, revisá la carpeta de spam.
          </div>
          {mail ? (
            <a
              href={mail.url}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full rounded-lg border border-accent px-4 py-2.5 text-center text-sm font-medium text-accent hover:bg-accent/10"
            >
              Abrir {mail.label}
            </a>
          ) : null}
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Restablecer contraseña"
      subtitle="Ingresá tu email y te mandamos un link para crear una nueva."
      footerLinks={
        authz.ok ? (
          <BackStepLink
            onClick={() => navigate(`/${partnerId}/login${window.location.search}`)}
            label="Volver al paso anterior"
          />
        ) : null
      }
    >
      <form
        onSubmit={onSubmit}
        aria-busy={loading ? 'true' : undefined}
        className="space-y-4"
        noValidate
      >
        <FormField
          name="email"
          type="email"
          label="Email"
          autoComplete="email"
          autoFocus
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        {err ? (
          <div
            role="alert"
            aria-live="polite"
            className="rounded-lg border border-error/30 bg-error/10 p-3 text-sm text-error"
          >
            {err}
          </div>
        ) : null}
        <SubmitButton loading={loading} loadingLabel="Enviando…">
          Enviar link
        </SubmitButton>
      </form>
    </AuthShell>
  );
}
